// Infoveranstaltungen zwischen den Terminen (Sitzung 14b, Daniel 22.10.2026: „Infoveranstaltungen sollen natürlich unter
// Termin und im Kalender stehen. Die Leute sollen das finden und buchen, ganz normal wie einen Termin.“). Die
// Infoveranstaltungen bleiben eigene Tabellen (nie eine Zeile in `termine`); die Seiten lesen sie nur über
// `marketing_info_kommende()` und mischen sie hier nach Datum unter die Termine. Ohne Importe, damit Webseite, neue
// Ansicht und Startseite die Regeln nehmen können, ohne etwas mitzuladen. Regeln: docs/regeln/admin-dashboard.md
// „Infoveranstaltungen“.

/** Name der Art auf Karten, im Auswahlfeld der Kategorien und als `typ` in gemischten Listen. */
export const INFO_TYP = "Infoveranstaltung";

/** Kommende Infoveranstaltung, wie `marketing_info_kommende()` sie liefert. */
export interface KommendeInfo {
  termin_id: string;
  slug: string;
  titel: string;
  untertitel: string | null;
  bild_url: string | null;
  beginn: string;
  dauer_minuten: number;
}

/** Eigene Anmeldung, wie `marketing_info_meine()` sie liefert („Meine Termine“). */
export interface MeineInfo extends KommendeInfo {
  anmeldung_id: string;
  token: string;
  angemeldet_am: string;
}

type Roh = Record<string, unknown>;
const text = (w: unknown): string | null => (typeof w === "string" && w.trim() ? w : null);

function kommendeZeile(z: unknown): KommendeInfo | null {
  const o = (z ?? {}) as Roh;
  const termin_id = text(o.termin_id);
  const slug = text(o.slug);
  const titel = text(o.titel);
  const beginn = text(o.beginn);
  if (!termin_id || !slug || !titel || !beginn) return null;
  return {
    termin_id,
    slug,
    titel: titel.trim(),
    untertitel: text(o.untertitel)?.trim() ?? null,
    bild_url: text(o.bild_url),
    beginn,
    dauer_minuten: typeof o.dauer_minuten === "number" && o.dauer_minuten > 0 ? o.dauer_minuten : 60,
  };
}

export function kommendeLesen(daten: unknown): KommendeInfo[] {
  return (Array.isArray(daten) ? daten : [])
    .map(kommendeZeile)
    .filter((z): z is KommendeInfo => z !== null)
    .sort((a, b) => a.beginn.localeCompare(b.beginn));
}

export function meineLesen(daten: unknown): MeineInfo[] {
  return (Array.isArray(daten) ? daten : [])
    .map((z) => {
      const k = kommendeZeile(z);
      const o = (z ?? {}) as Roh;
      const anmeldung_id = text(o.anmeldung_id);
      const token = text(o.token);
      return k && anmeldung_id && token ? { ...k, anmeldung_id, token, angemeldet_am: text(o.angemeldet_am) ?? "" } : null;
    })
    .filter((z): z is MeineInfo => z !== null)
    .sort((a, b) => a.beginn.localeCompare(b.beginn));
}

/**
 * Wo sich jemand anmeldet: die Landingpage mit dem gewählten Termin (`?termin=`) und der Stelle, von der aus geklickt
 * wurde (`?von=`, landet als Quelle bei der Anmeldung, etwa „von=termine“).
 */
export function infoAdresse(i: Pick<KommendeInfo, "slug" | "termin_id">, von?: string): string {
  const params = new URLSearchParams({ termin: i.termin_id });
  if (von) params.set("von", von);
  return `/info/${encodeURIComponent(i.slug)}?${params.toString()}`;
}

/** Filter der Terminseite (wie in `Termine.tsx`): Kategorie, Schnellfilter, Suche. */
export interface TerminFilterLage {
  kategorie: string;
  schnell: string;
  suche: string;
}

/**
 * Passt eine Infoveranstaltung zu den Filtern der Terminseite? Unter „Alle“ und unter der eigenen Kategorie ja, unter
 * jeder anderen Kategorie nein; „Kostenlos“ ja (sie sind immer kostenlos), „Diese Woche“ wie bei den Terminen (bis
 * sieben Tage nach dem heutigen Tagesbeginn), „Themenwebinare“ nein; die Suche geht über Titel, Untertitel und die Art.
 */
export function infoPasst(i: KommendeInfo, f: TerminFilterLage, jetzt: Date = new Date()): boolean {
  if (f.kategorie !== "alle" && f.kategorie !== INFO_TYP) return false;
  if (f.schnell === "themenwebinare") return false;
  if (f.schnell === "diese-woche") {
    const grenze = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() + 7);
    if (new Date(i.beginn) > grenze) return false;
  }
  const q = f.suche.trim().toLowerCase();
  if (!q) return true;
  return [i.titel, i.untertitel ?? "", INFO_TYP].some((t) => t.toLowerCase().includes(q));
}

/** Ein Eintrag einer gemischten Liste: ein Termin oder eine Infoveranstaltung, mit dem Datum zum Sortieren. */
export type Eintrag<T> = { art: "termin"; termin: T; datum: string } | { art: "info"; info: KommendeInfo; datum: string };

/** Termine und Infoveranstaltungen nach Datum gemischt (beide Listen bleiben in ihrer Reihenfolge, wenn gleich). */
export function mischen<T>(termine: readonly T[], infos: readonly KommendeInfo[], datumVon: (t: T) => string): Eintrag<T>[] {
  const alle: Eintrag<T>[] = [
    ...termine.map((termin) => ({ art: "termin" as const, termin, datum: datumVon(termin) })),
    ...infos.map((info) => ({ art: "info" as const, info, datum: info.beginn })),
  ];
  return alle
    .map((e, stelle) => ({ e, stelle, zeit: new Date(e.datum).getTime() }))
    .sort((a, b) => a.zeit - b.zeit || a.stelle - b.stelle)
    .map((x) => x.e);
}

/** Gemischt und nach Monat gruppiert; `monatVon` ist dieselbe Formel wie die der Seite („November 2026“). */
export function nachMonatGemischt<T>(
  termine: readonly T[],
  infos: readonly KommendeInfo[],
  datumVon: (t: T) => string,
  monatVon: (iso: string) => string,
): { monat: string; eintraege: Eintrag<T>[] }[] {
  const gruppen: { monat: string; eintraege: Eintrag<T>[] }[] = [];
  for (const e of mischen(termine, infos, datumVon)) {
    const monat = monatVon(e.datum);
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && letzte.monat === monat) letzte.eintraege.push(e);
    else gruppen.push({ monat, eintraege: [e] });
  }
  return gruppen;
}

/** „Kostenlos · online über Zoom“ und der Satz unter der Karte: Infoveranstaltungen sind immer kostenlos und online. */
export const INFO_SATZ = "Kostenlos, online und live über Zoom";

/** „JJJJ-MM-TT“ eines Zeitpunkts in Berliner Zeit. */
function berlinerTag(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: "Europe/Berlin" });
}

/**
 * Countdown auf der Landingpage (22.10.2026, Daniel: „machen wir eine richtig geile Landingpage. Denkt daran, dass die
 * Leute sich anmelden müssen.“): „Heute“, „Morgen“, „In 11 Tagen“, nach Berliner Kalendertagen; vorbei ergibt `null`.
 */
export function tageBisText(beginn: string, jetzt: Date = new Date()): string | null {
  const start = new Date(beginn);
  if (Number.isNaN(start.getTime()) || start.getTime() < jetzt.getTime()) return null;
  const tage = Math.round((Date.parse(berlinerTag(start)) - Date.parse(berlinerTag(jetzt))) / 86_400_000);
  if (tage <= 0) return "Heute";
  if (tage === 1) return "Morgen";
  return `In ${tage} Tagen`;
}
